import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Delete, Search } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { textFilterToRegExp, useLocales } from '../../../../hooks';
import Button from '../../../shared/Button';
import { DataGrid, DataGridColDef } from '../../../shared/DataGrid';
import TextField from '../../../shared/TextField';
import { PermissionsGroupId, SponsorResponse } from '../../../../API';
import { sponsorManagerTestIds } from '../../../shared/TestsIds';
import IconButton from '../../../shared/IconButton';
import { useData } from '../../../../data-layer';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { useRecoilState, useRecoilValue } from 'recoil';
import { HomepageOptions } from '../../../../state/theme';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { withSponsorsSplitPane } from '../../../../state/General';
import { AppRoutes } from '../../../../Routes';
import { withSponsorsFilters } from '../../../../state/Payments/withPaymentsFilters';
import { IdBadge } from '../../../shared/IdBadge';
import Image from '../../../shared/Image';
import { getAssetUrl } from '../../../../utils/assetHelpers';

const LOGO_WIDTH = 42;

const useStyles = makeStyles()((theme) => ({
  root: { display: 'flex', flexDirection: 'column', flexGrow: 1, padding: theme.spacing(4) },
  filters: { display: 'flex', gap: theme.spacing(4), marginBottom: theme.spacing(4) },
  textFilterContainer: { flexGrow: 1, display: 'flex', gap: theme.spacing(4) },
  textFilter: { width: 500, marginBottom: '0 !important' },
  gridContainer: { display: 'flex', flexGrow: 1 },
  assetImage: { width: '100%', maxHeight: LOGO_WIDTH, objectFit: 'contain' }
}));

export function SponsorManager(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const navigate = useNavigate();
  const {
    sponsors: {
      state: { withAllRecords: withAllSponsors },
      hook: { new: newSponsor, edit: editSponsor, remove: removeSponsor }
    }
  } = useData();
  const sponsors = useRecoilValue(withAllSponsors);

  const { canSave, canDelete, canRead } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const { hasUpsertPermission, hasDeletePermission, hasReadPermission } = usePermissions();

  const hasMonetizationReadPermissions = hasReadPermission(PermissionsGroupId.MONETIZATION) || canRead;

  const [filters, setFilters] = useRecoilState(withSponsorsFilters);
  const [filteredSponsors, setFilteredSponsors] = useState<SponsorResponse[] | undefined>([]);

  useEffect(() => {
    if (!hasMonetizationReadPermissions) {
      navigate(AppRoutes.welcome);
    }
  }, [hasMonetizationReadPermissions]);

  useEffect(() => {
    updateFilteredSponsors();
  }, [sponsors, filters]);

  const isLoaded = !!sponsors;

  const deleteSponsorHandler = (sponsor: SponsorResponse) => {
    if (window.confirm(t('payments.sponsor.delete_confirmation'))) {
      removeSponsor(sponsor.id);
    }
  };

  const columns: DataGridColDef<SponsorResponse>[] = [
    {
      key: 'name',
      label: t('payments.sponsor.name'),
      cellRenderer: (s) => s.name,
      baseWidth: '20%'
    },
    {
      key: 'slogan',
      label: t('payments.sponsor.slogan'),
      cellRenderer: (s) => s.slogan,
      baseWidth: '20%'
    },
    {
      key: 'logo',
      label: t('payments.sponsor.logo'),
      cellRenderer: (s) => (
        <Image className={classes.assetImage} src={`${getAssetUrl(s.logo)}?tr=w-${LOGO_WIDTH * 2}`} />
      ),
      baseWidth: '15%'
    },
    {
      key: 'website',
      label: t('payments.sponsor.website'),
      cellRenderer: (s) => <IdBadge id={s.website} />,
      baseWidth: '25%'
    },
    {
      key: 'actions',
      label: t('general.actions'),
      cellRenderer: (sponsor) => (
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            deleteSponsorHandler(sponsor);
          }}
          title={t('general.delete')}
          size="large"
          disabled={!hasDeletePermission(PermissionsGroupId.MONETIZATION) && !canDelete}
          data-testid={sponsorManagerTestIds.deleteSponsorButton}
        >
          <Delete />
        </IconButton>
      ),
      baseWidth: '10%'
    }
  ];

  const onSponsorClick = (sponsor: SponsorResponse) => {
    if (!isLoaded) return;
    editSponsor(sponsor.id);
  };

  const onTextFilterChanged: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
    setFilters({
      ...filters,
      text: evt.target.value
    });
  };

  const updateFilteredSponsors = () => {
    if (!sponsors) return;
    const newFilteredPromotions = sponsors.filter((sponsor) => {
      if (filters.text) {
        const textFilterRgx = textFilterToRegExp(filters.text);
        return (
          textFilterRgx.test(sponsor.name) ||
          (sponsor.slogan && textFilterRgx.test(sponsor.slogan)) ||
          textFilterRgx.test(sponsor.website)
        );
      }
      return true;
    });
    setFilteredSponsors(newFilteredPromotions);
  };

  return (
    <div className={classes.root} data-testid={sponsorManagerTestIds.root}>
      <div className={classes.filters}>
        <div className={classes.textFilterContainer}>
          <TextField
            clearable
            label={t('payments.sponsor.filter_by_text')}
            className={classes.textFilter}
            value={filters.text}
            onChange={onTextFilterChanged}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            data-testid={sponsorManagerTestIds.textFilter}
          />
        </div>
        <Button
          endIcon={<Add />}
          disabled={!isLoaded || (!hasUpsertPermission(PermissionsGroupId.MONETIZATION) && !canSave)}
          onClick={newSponsor}
          data-testid={sponsorManagerTestIds.newSponsorButton}
        >
          {t('payments.sponsor.new_sponsor')}
        </Button>
      </div>
      <div className={classes.gridContainer}>
        <DataGrid
          rows={filteredSponsors}
          columns={columns}
          onRowClick={(sponsor) => onSponsorClick(sponsor)}
          withColumnSizes={withSponsorsSplitPane}
          useVirtualization
          rowKey="id"
        />
      </div>
    </div>
  );
}
